import { formatInTimeZone } from 'date-fns-tz';

import { Lane } from './types';

export type LongHaulMoveTarget = {
  shiftTruckId: number;
  targetRowId: string;
  label: string;
  subLabel: string;
};

const BUSINESS_TIME_ZONE = 'Asia/Tokyo';

/**
 * ISO → 業務日（JST）の日付と時刻。不正な日時は null。
 *
 * Why not date-fns の format: timeZone を無視するため実行環境のタイムゾーンで
 * 出てしまう（FE 既存バグ #2376）。実際に JST へ変換する formatInTimeZone を使う。
 */
const jstPartsOf = (iso: string): { day: string; time: string } | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: formatInTimeZone(date, BUSINESS_TIME_ZONE, 'M/d'),
    time: formatInTimeZone(date, BUSINESS_TIME_ZONE, 'H:mm'),
  };
};

/**
 * 勤務の期間ラベル。同日なら '6/8 8:00〜18:00'、日をまたぐなら '6/8 8:00〜6/10 18:00'。
 *
 * Why not 日付だけにしない: 同じ車番・同じ日に勤務時間だけ違う候補が並ぶと、
 * 日付だけのラベルでは見分けられない。同日は終わりの日付を省いて冗長さを避ける。
 */
const periodOf = (startAt: string, endAt: string): string => {
  const start = jstPartsOf(startAt);
  if (start == null) return '';
  const end = jstPartsOf(endAt);
  if (end == null) return `${start.day} ${start.time}`;
  return start.day === end.day
    ? `${start.day} ${start.time}〜${end.time}`
    : `${start.day} ${start.time}〜${end.day} ${end.time}`;
};

/**
 * 「他レーンへ移す」の候補を勤務単位で作る。
 *
 * Why not buildMoveTargets: あちらは shiftId で重複排除する。1 shift が複数
 * トラックにまたがる勤務（MapToolbar.test.tsx の契約7）を潰すうえ、ラベルに
 * 日付が無いので 7 日ぶんの候補を見分けられない。
 *
 * Why source が不明なら空にする: 候補を出しても選んだ後に必ず失敗する。
 * 押せるのに必ず失敗する選択肢を作らない（buildMoveTargets と同じ方針）。
 */
export function buildLongHaulMoveTargets(
  lanes: Lane[],
  source: { shiftTruckId: number | null },
): LongHaulMoveTarget[] {
  if (source.shiftTruckId == null) return [];

  const targets: LongHaulMoveTarget[] = [];
  for (const lane of lanes) {
    for (const work of lane.workTargets) {
      if (work.shiftTruckId === source.shiftTruckId) continue;
      targets.push({
        shiftTruckId: work.shiftTruckId,
        targetRowId: work.targetRowId,
        // 車番は plateNumber。vehicleNumber は後方互換フィールドで車両名が入っている
        label: lane.vehicle.plateNumber || '車番未設定',
        subLabel: [lane.vehicle.driverName, periodOf(work.startAt, work.endAt)].filter(Boolean).join('・'),
      });
    }
  }
  return targets;
}
